import { Box, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import dayjs from "dayjs";
import React from "react";
import { Order } from "../../../types/order";

interface OrderInvoicesPdfProps {
    orders: Order[];
    global: any;
    isGeneratingPDF: boolean;
}

export const OrderInvoicesPdf = React.forwardRef<HTMLDivElement, OrderInvoicesPdfProps>((props, ref) => {
    return (
        <Box ref={ref} className={`${props.isGeneratingPDF ? "visible-content" : "hidden-content"}`} sx={{ backgroundColor: '#ffffff' }}>
            {props.orders.map((order: Order, index: number) => (
                <Box 
                    key={order.idOrder} 
                    sx={{ 
                        height: '297mm',
                        width: '210mm', 
                        position: 'relative',
                        pageBreakAfter: index < props.orders.length - 1 ? 'always' : 'auto',
                        padding: '15mm',
                        backgroundColor: '#ffffff',
                        boxSizing: 'border-box'
                    }}
                >
                    <Box sx={{ mt: 1, mb: 2, width: '100%' }}>
                        <Grid container justifyContent="space-between" alignItems="flex-start" sx={{ overflow: 'visible' }}>
                            <Grid xs={3}>
                                <Typography variant="body2" sx={{ fontSize: '12px', lineHeight: 1.5 }}>
                                    Nr. Unik: <b>{props.global.companyDetails.businessNumber}</b>
                                    <br />
                                    Nr. TVSH: <b>{props.global.companyDetails.taxNumber}</b>
                                    <br />
                                    Tel: <b>{props.global.companyDetails.phone}</b>
                                    <br />
                                    Adresa: <b>{props.global.companyDetails.address}</b>
                                    <br />
                                    Nr: <b>{order.idOrder}</b>
                                    <br />
                                    Data: <b>{order.orderDate ? dayjs(order.orderDate).format('DD.MM.YYYY') : ''}</b>
                                </Typography>
                            </Grid>
                            <Grid xs={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflow: 'visible' }}>
                                <Box
                                    sx={{
                                        width: '100%',
                                        maxWidth: '250px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        padding: '5px 0',
                                        overflow: 'visible'
                                    }}
                                >
                                    <img
                                        src="/assets/Lockup-Color.png"
                                        alt="Lockup"
                                        crossOrigin="anonymous"
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            maxWidth: '250px',
                                            display: 'block',
                                            objectFit: 'contain'
                                        }}
                                    />
                                </Box>
                            </Grid>
                            <Grid xs={3}>
                                <Typography variant="body2" sx={{ textAlign: { xs: 'right' }, fontSize: '12px', lineHeight: 1.5 }}>
                                    Shitësi: <b>{`${order.idClient?.firstName} ${order.idClient?.lastName}`}</b>
                                    <br />
                                    {order.idClient?.phone && (
                                        <>
                                            Tel: <b>{order.idClient.phone}</b>
                                            <br />
                                        </>
                                    )}
                                    ID: <b>{order.idClient?.nationalId}</b>
                                    {order.idClient?.idMunicipality?.municipalityName && (
                                        <>
                                            <br />
                                            <b>{order.idClient.idMunicipality.municipalityName}</b>
                                        </>
                                    )}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <Table sx={{ mt: 8, border: '2px solid black' }}>
                        <TableHead sx={{ border: '2px solid black' }}>
                            <TableRow>
                                <TableCell sx={{ py: 1.2, fontSize: '13px', fontWeight: 600 }}>Lloji</TableCell>
                                <TableCell sx={{ py: 1.2, fontSize: '13px', fontWeight: 600 }}>Sasia (kg)</TableCell>
                                <TableCell sx={{ py: 1.2, fontSize: '13px', fontWeight: 600 }}>Çmimi</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow sx={{ border: '2px solid black' }}>
                                <TableCell sx={{ borderBottom: '2px solid black', py: 1.2, fontSize: '12px' }}>{order.type}</TableCell>
                                <TableCell sx={{ borderBottom: '2px solid black', py: 1.2, fontSize: '12px' }}>{order.quantity_per_kg}kg</TableCell>
                                <TableCell sx={{ borderBottom: '2px solid black', py: 1.2, fontSize: '12px' }}>{order.price}&euro;</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell colSpan={2} sx={{ borderBottom: 'none', borderTop: '2px solid black', py: 1.2 }} />
                                <TableCell align="right" sx={{ borderBottom: 'none', borderTop: '2px solid black', py: 1.2 }}>
                                    <Typography variant="subtitle2">
                                        <span style={{ fontWeight: 700, fontSize: '15px', marginRight: '1rem' }}>Totali:</span>
                                        <span style={{ fontSize: '13px' }}>{order.totalPrice}&euro;</span>
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>

                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: '15mm',
                            left: '15mm',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                        }}
                    >
                        <Typography sx={{ fontWeight: 600, fontSize: '16px', mr: 9, fontFamily: 'Times New Roman', mb: 1 }}>
                            Depoisti
                        </Typography>
                        <Typography sx={{ width: '10.5rem', borderBottom: '2px solid black', height: '7.2rem', mr: 2 }}></Typography>
                    </Box>

                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: '15mm',
                            right: '15mm',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        <Typography sx={{ fontWeight: 600, fontSize: '16px', fontFamily: 'Times New Roman', mb: 1 }}>
                            Nënshkrimi
                        </Typography>
                        {order.idClient?.digitalSignature ? (
                            <Box sx={{ width: '10.5rem', height: '7.2rem', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                                <img
                                    src={order.idClient.digitalSignature}
                                    alt="Signature"
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ width: '10.5rem', height: '7.2rem' }}></Box>
                        )}
                        <Typography
                            sx={{
                                width: '10.5rem',
                                borderBottom: '2px solid black',
                            }}
                        ></Typography>
                    </Box>
                </Box>
            ))}
        </Box>
    );
});
